<template>
  <div>
    <public-announcement />
    <div class="flex items-center justify-center min-h-screen p-4 overflow-hidden">
      <div class="relative z-10 w-108">
        <div class="flex justify-center">
          <span class="pt-10 pb-14" v-if="!isPresenterWhiteLabel">
            <a href="https://ahaslides.com">
              <aha-logo
                width="140"
                height="24"
                viewBox="0 0 9600 1602"
              />
            </a>
          </span>
        </div>

        <!-- Form -->
        <div class="shadow-panel rounded-3xl bg-base-0 px-9 pt-10 pb-12">
          <h2 class="mb-4 text-center text-2xl w-80 mx-auto" v-if="isTrialLink">
            {{ $i18nPresenter.t('Log in to get AhaSlides One-month Free trial') }}
          </h2>
          <h2 class="mb-4 text-center text-2xl" v-else>
            {{ $i18nPresenter.t('Log in') }}
          </h2>
          <div v-if="!isPresenterWhiteLabel">
            <p class="text-sm text-center mb-8">
              {{ $i18nPresenter.t('No account?') }}
              <a @click="goSignup()" class="font-bold text-link--primary">
                {{ $i18nPresenter.t('Sign up now') }}
              </a>
            </p>

            <google-button-v2 type="login">
              {{ $i18nPresenter.t('Log in with Google') }}
            </google-button-v2>

            <div class="separator my-6">
              {{ $i18nPresenter.t('or') }}
            </div>
          </div>
          <form @submit.prevent="onSubmitLogin">
            <aha-input-group class="mt-6 mb-4">
              <span class="aha-input-prepend">
                <aha-icon name="mail" />
              </span>

              <aha-input
                type="email"
                class="w-full"
                :placeholder="$i18nPresenter.t('Your email')"
                required
                v-model="email"
              />
            </aha-input-group>

            <aha-input-group class="relative mb-2">
              <span class="aha-input-prepend">
                <aha-icon name="lock" />
              </span>

              <aha-input
                :type="showPassword ? 'text' : 'password'"
                class="w-full aha-input_limit-char"
                :placeholder="$i18nPresenter.t('Your password')"
                size="md"
                required
                v-model="password"
              />

              <span
                class="absolute flex items-center cursor-pointer inset-y-2 right-3"
                @click="showPassword = !showPassword"
              >
                <font-awesome-icon
                  class="text-base-80"
                  :icon="!showPassword ? ['fas', 'eye'] : ['fas', 'eye-slash']"
                />
              </span>
            </aha-input-group>

            <p class="text-sm mb-6">
              <router-link
                :to="'/pages/forgot-password' + (isFromMSTeams ? '?ms_teams=true' : '')"
                class="text-base-70 underline text-underline-offset"
              >
                {{ $i18nPresenter.t('Forgot password?') }}
              </router-link>
            </p>

            <div class="mb-4 text-sm text-red-100" v-if="textError">
              <font-awesome-icon
                :icon="['fas', 'exclamation-triangle']"
              />
              <span v-if="textError === 'default_error'">
                {{ $i18nPresenter.t("Oops, there's an error while logging you in. Please try again.") }}
                <span
                  v-html="$i18nPresenter.t('ifTheProblemPersists')"
                ></span>
              </span>
              <span v-else>
                {{ textError }}
              </span>
            </div>

            <div class="text-center form-group mb-4">
              <div class="col-xs-12">
                <vue-recaptcha
                  :sitekey="keyRecaptcha"
                  @verify="onVerify"
                  @error="onRecaptchaError"
                  size="invisible"
                  @expired="onExpired"
                  :load-recaptcha-script="true"
                  recaptcha-host="www.recaptcha.net"
                  ref="recaptcha"
                  class="auth-captcha"
                />
                <aha-button
                  type="submit"
                  class="w-full"
                  size="lg"
                  :disabled="isDisabled"
                >
                  <span>{{ $i18nPresenter.t('Log In') }}</span>
                  <aha-spinner v-if="submitting" variant="white" />
                </aha-button>
              </div>
            </div>

            <p class="text-center" v-if=" !$store.state.isPresenterWhiteLabel">
              <a
                class="font-bold text-link--primary"
                @click="handleLoginWithSso()"
              >
                {{ $i18nPresenter.t('Log in with SSO') }}
              </a>
            </p>
          </form>
        </div>

        <span class="absolute bottom-0 hidden lg:block -right-20">
          <img src="@/assets/images/elements/tree.svg">
        </span>

        <div class="h-28" />
      </div>

      <back-drop />

      <span class="absolute hidden lg:block top-1/4 right-2/3">
        <img src="@/assets/images/elements/music-note.svg">
      </span>

      <span class="absolute hidden lg:block top-2/4 left-1/4">
        <img src="@/assets/images/elements/pallet.svg">
      </span>
    </div>
  </div>
</template>

<script>
import VueRecaptcha from 'vue-recaptcha'
import { mapState } from 'vuex'

import { authentication, app } from '@microsoft/teams-js'
import Util from '@/util'
import Config from '@/config'
import cookie from '@/services/cookie'
import { PRESENTER_LOGIN } from '@/constant/eventCategories'
import { isTrialLink, scheduleShowEnjoyTrialPopup, scheduleShowTrialNotAvailablePopup } from '@/utils/trial-plan.utils'

import PublicAnnouncement from '@/components-v2/common/global-notification/PublicAnnouncement.vue'
import BackDrop from '@/components-v2/common/auth/BackDrop.vue'
import GoogleButtonV2 from '@/components-v2/common/auth/GoogleButtonV2.vue'

export default {
  name: 'Login',
  components: {
    VueRecaptcha,
    BackDrop,
    GoogleButtonV2,
    PublicAnnouncement,
  },
  data() {
    return {
      email: '',
      password: '',
      checkbox_remember_me: true,
      textError: '',
      submitting: false,
      keyRecaptcha: Config.keyRecapcha,
      tokenRecaptcha: '',
      showPassword: false,
      isGuest: false,
      isFromMSTeams: false,
      isTrialLink: false,
    }
  },
  computed: {
    ...mapState({
      isPresenterWhiteLabel: (state) => state.isPresenterWhiteLabel,
    }),
    inviterName() {
      return Util.getParamValue('inviter-name', window.location.href)
    },
    inviterEmail() {
      return Util.getParamValue('inviter-email', window.location.href)
    },
    sharedPresentationName() {
      return Util.getParamValue('shared-presentation', window.location.href)
    },
    isDisabled() {
      return !(this.email || this.password)
    },
  },
  async created() {
    const isFromMSTeams = Util.getParamValue('ms_teams', window.location.href)
    if (isFromMSTeams) {
      localStorage.setItem('ms_teams', 'true')
      await app.initialize()
    }
    this.isTrialLink = isTrialLink(window.location.href)
    this.isGuest = !!cookie.getValue('aha-guest-user')
    if (cookie.getValue('ahaToken') && !this.isGuest) {
      const redirect = Util.getParamValue('redirect', window.location.href)
      if (isFromMSTeams) {
        authentication.notifySuccess()
      }
      if (redirect) {
        window.location.href = redirect
        return
      }
      this.$router.push('/apps/presentations')
    }
  },
  mounted() {
    document.body.classList.remove('no-scroll')
    Util.mixpanelTracking(
      PRESENTER_LOGIN,
      'Open login form',
    )
    const isFromMSTeams = !!Util.getParamValue('ms_teams', window.location.href)

    this.isFromMSTeams = isFromMSTeams
  },
  methods: {
    onVerify(response) {
      this.tokenRecaptcha = response
      this.login()
    },
    onSubmitLogin() {
      if (this.submitting) return
      this.submitting = true
      if (this.email.match(/qa\.ahaslides\+(.*)@gmail.com/i)) {
        this.login()
      } else {
        this.$refs.recaptcha.execute()
        Util.removeRecaptchaCover()
      }
    },
    onExpired() {
      this.$refs.recaptcha.reset()
      this.tokenRecaptcha = ''
    },
    setLang(lang) {
      this.$store.dispatch('setLang', lang)
    },

    onRecaptchaError() {
      this.submitting = false
      this.textError = 'Recaptcha error. If the problem still occurs, please check your internet connection.'
    },

    goSignup() {
      const content = Util.getParamValue('content', window.location.href)
      const source = Util.getParamValue('source', window.location.href)
      const promo = Util.getParamValue('promo', window.location.href)
      const redirect = Util.getParamValue('redirect', window.location.href)
      const code = Util.getParamValue('code', window.location.href)

      let query = {}

      if (content && source === 'pricing') {
        query = {
          content,
          source,
        }

        if (promo) {
          query.promo = promo
        }
      }

      if (this.isFromMSTeams) {
        query.ms_teams = 'true'
      }

      if (redirect) {
        query.redirect = redirect
      }
      if (code) {
        query.code = code
      }

      this.$router.push({
        path: '/pages/register',
        query,
      })
    },

    login() {
      // eslint-disable-next-line promise/catch-or-return
      this.$http
        .post(Config.api.login, {
          email: this.email,
          password: this.password,
          tokenRecaptcha: this.tokenRecaptcha,
          isTrial: this.isTrialLink,
        })
        .then(
          (res) => {
            this.submitting = false
            this.$store.dispatch('setCurrentUserAction', res.body.user)

            Util.setCookieToken(res.body.token)
            if (this.isGuest) {
              cookie.deleteValue('aha-guest-user')
            }

            Object.assign(this.$http.headers, {
              common: { Authorization: `Bearer ${res.body.token}` },
            })
            const redirect = Util.getParamValue('redirect', window.location.href)
            Util.mixpanelTracking(
              PRESENTER_LOGIN,
              'Login Button > Submit > Succeeded',
              '',
            )

            Util.trackGA4AndMixpanel('login', {
              method: 'with form login',
            })
            const { user } = res.body
            Util.ga4Config({
              user_id: user.id,
            })

            const content = Util.getParamValue('content', window.location.href)
            const source = Util.getParamValue('source', window.location.href)
            if (this.isFromMSTeams) {
              authentication.notifySuccess(res.body.token)
            }

            if (this.isTrialLink) {
              if (res.body.user.hasUpgradedToTrial) {
                scheduleShowEnjoyTrialPopup()
              } else if (res.body.user.plan !== 'trial-plus-monthly') {
                scheduleShowTrialNotAvailablePopup()
              }
            }

            let isPlan = false
            if (
              content === 'essential_annually' ||
              content === 'pro_annually' ||
              content === 'essential_monthly' ||
              content === 'pro_monthly' ||
              content === 'plus_monthly' ||
              content === 'plus_annually' ||
              content === 'edu_small' ||
              content === 'edu_medium' ||
              content === 'edu_large' ||
              content === 'one_time_mini' ||
              content === 'one_time_small' ||
              content === 'one_time_medium' ||
              content === 'one_time_large'
            ) {
              isPlan = true
            }
            // eslint-disable-next-line promise/always-return
            if (redirect) {
              window.location.href = redirect
            } else if (source === 'pricing' && isPlan) {
              let plan = 'pro-yearly'
              if (content === 'essential_annually') {
                plan = 'essential-yearly'
              }
              if (content === 'essential_monthly') {
                plan = 'essential-monthly'
              }
              if (content === 'pro_monthly') {
                plan = 'pro-monthly'
              }
              if (content === 'plus_monthly') {
                plan = 'plus-monthly'
              }
              if (content === 'plus_annually') {
                plan = 'plus-yearly'
              }
              if (content === 'edu_small') {
                plan = 'edu-small-yearly'
              }
              if (content === 'edu_medium') {
                plan = 'edu-medium-yearly'
              }
              if (content === 'edu_large') {
                plan = 'edu-large-yearly'
              }
              if (content === 'one_time_mini') {
                plan = 'one-time-mini'
              }
              if (content === 'one_time_small') {
                plan = 'one-time-small'
              }
              if (content === 'one_time_medium') {
                plan = 'one-time-medium'
              }
              if (content === 'one_time_large') {
                plan = 'one-time-large'
              }
              const promo = Util.getParamValue('promo', window.location.href)
              let urlRedirect =
                `${Config.presenterDomain}/apps/upgrade/${plan}`
              if (promo) {
                urlRedirect = `${urlRedirect}?promo=${promo}`
              }
              window.location.href = urlRedirect
            } else {
              this.$router.push({
                path: '/apps/presentations',
              })
            }
            localStorage.removeItem('blockAudiences')
          },
          (err) => {
            this.submitting = false
            this.$refs.recaptcha.reset()
            if (err.body.message) {
              this.textError = err.body.message
            } else {
              this.textError = 'default_error'
            }
            Util.mixpanelTracking(
              PRESENTER_LOGIN,
              'Login Button > Submit > Failed',
              JSON.stringify(err.body),
            )
          },
        )

      Util.mixpanelTracking(
        PRESENTER_LOGIN,
        'Login Button > Submit',
        '',
      )
    },

    registerUser() {
      this.$router.push('/pages/register')
    },

    handleLoginWithSso() {
      this.$router.push('/pages/sso')
    },

  },
}
</script>

<style lang="scss" scoped>
.separator {
  @apply text-base-90;
  display: flex;
  align-items: center;
  text-align: center;
}

.separator::before,
.separator::after {
  @apply border-base-30 border-b;
  content: '';
  flex: 1;
}

.separator:not(:empty)::before {
  @apply mr-5;
}

.separator:not(:empty)::after {
  @apply ml-5;
}

.text-underline-offset {
  text-underline-offset: 0.25rem;
}
</style>
